import React, { useCallback, useEffect, useState } from 'react';
import { Receipt, Pencil, Save, X as XIcon, Loader2, CheckCircle2 } from 'lucide-react';
import { propertySettingsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

/**
 * iter170 — Editable GST Number card, scoped to the currently-selected
 * property. Reads from and writes to /api/property-settings/{property_id}.
 *
 * Data isolation:
 *   • The effect re-loads when `propertyId` changes (property switcher).
 *   • Old state is fully reset so one property's GSTIN can never leak into
 *     another's view.
 */
const PropertyGstCard = ({ propertyId }) => {
  const { toast } = useToast();
  const [loaded, setLoaded]   = useState(false);
  const [saving, setSaving]   = useState(false);
  const [editing, setEditing] = useState(false);
  const [saved, setSaved]     = useState('');   // last-persisted value
  const [draft, setDraft]     = useState('');   // in-edit value

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoaded(false);
    setEditing(false);
    setSaved('');
    setDraft('');
    try {
      const res = await propertySettingsAPI.get(propertyId);
      const v = res?.RESULT?.gst_number || '';
      setSaved(v);
      setDraft(v);
    } catch (err) {
      // Non-fatal — card just shows empty state
      // console.warn('GST load failed', err);
    } finally {
      setLoaded(true);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => { setDraft(saved); setEditing(true); };
  const cancelEdit = () => { setDraft(saved); setEditing(false); };

  const submit = async () => {
    const clean = (draft || '').replace(/\s+/g, '').toUpperCase();
    setSaving(true);
    try {
      const res = await propertySettingsAPI.update(propertyId, { gst_number: clean });
      const persisted = res?.RESULT?.gst_number || '';
      setSaved(persisted);
      setDraft(persisted);
      setEditing(false);
      toast({
        title: 'GST Number saved',
        description: persisted ? persisted : 'GST Number cleared.',
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save GST Number.';
      toast({ title: 'Save failed', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 shadow-premium overflow-hidden"
      data-testid="profile-gst-card"
    >
      <div className="p-4 border-b border-gray-100 bg-[#EEF1FC]/50 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 tracking-tight">
          <Receipt className="w-4 h-4 text-[#1B2A7B]" />
          GST Number
        </h3>
        {loaded && !editing && (
          <button
            type="button"
            onClick={startEdit}
            className="text-sm font-medium text-[#1B2A7B] hover:underline flex items-center gap-1"
            data-testid="profile-gst-edit-btn"
          >
            <Pencil className="w-3.5 h-3.5" />
            {saved ? 'Edit' : 'Add'}
          </button>
        )}
      </div>

      <div className="p-4">
        {!loaded && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {loaded && !editing && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#EEF1FC] rounded-lg flex items-center justify-center flex-shrink-0">
              <Receipt className="w-5 h-5 text-[#1B2A7B]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400">GSTIN</p>
              {saved ? (
                <p className="text-gray-900 font-mono tracking-wide" data-testid="profile-gst-value">
                  {saved}
                </p>
              ) : (
                <p className="text-gray-500 italic text-sm" data-testid="profile-gst-empty">
                  Not configured — add your GST Number to include GSTIN on invoices.
                </p>
              )}
            </div>
          </div>
        )}

        {loaded && editing && (
          <div className="space-y-3">
            <label className="text-xs text-gray-500 block">
              GSTIN <span className="text-gray-400">(15 characters, e.g. 22AAAAA0000A1Z5)</span>
            </label>
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value.toUpperCase())}
              placeholder="Enter GST Number"
              maxLength={20}
              className="w-full px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-900 font-mono text-sm focus:border-[#1B2A7B] focus:ring-1 focus:ring-[#1B2A7B] tracking-wide"
              data-testid="profile-gst-input"
              autoFocus
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1B2A7B] text-white text-sm font-medium hover:bg-[#152163] disabled:opacity-60"
                data-testid="profile-gst-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 disabled:opacity-60"
                data-testid="profile-gst-cancel-btn"
              >
                <XIcon className="w-4 h-4" />
                Cancel
              </button>
              {saved && (
                <span className="ml-auto text-[11px] text-emerald-600 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Saved
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyGstCard;
